import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import {
  hasWallet,
  waitForInjectedWallet,
  walletConnectAvailable,
  signInWithEthereum,
  signInWithWalletConnect,
  SiweUserRejected,
} from './siwe.js';

/**
 * useConnect — wallet sign-in as a one-call hook, for surfaces that need to
 * connect in place (e.g. the Buy page) rather than bouncing to the landing
 * page. Mirrors the landing flow: prefer an injected wallet, fall back to
 * WalletConnect, verify with the server, then refresh the session.
 *
 * `connect()` resolves to `true` on a verified sign-in, `false` otherwise, so
 * callers can chain the next step (open the pay dialog) only on success.
 */
export function useConnect() {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async (): Promise<{ ok: boolean; holder: boolean }> => {
    setConnecting(true);
    setError(null);
    try {
      const getNonce = async (): Promise<string> => (await api.nonce()).nonce;
      const useInjected = hasWallet() || (await waitForInjectedWallet());
      const out = useInjected
        ? await signInWithEthereum(await getNonce())
        : walletConnectAvailable()
          ? await signInWithWalletConnect(getNonce)
          : null;
      if (!out) {
        setError(
          'No wallet detected. Open this page in your wallet app’s browser, or install a browser wallet and refresh.',
        );
        return { ok: false, holder: false };
      }
      const res = await api.verify(out.message, out.signature);
      // First connect after an anonymous free gilding: claim it into the new
      // wallet's gallery so the free piece is saved like any other — holders AND
      // non-holders keep it. Best-effort and idempotent.
      try {
        const pendingFree = window.localStorage.getItem('gilding_free_pending');
        if (pendingFree) {
          await api.claimFreeGild(pendingFree);
          window.localStorage.removeItem('gilding_free_pending');
          await queryClient.invalidateQueries({ queryKey: ['mine'] });
        }
      } catch {
        /* claim is non-critical; leave the marker so a later sign-in retries */
      }
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      return { ok: true, holder: res.holder };
    } catch (e) {
      if (e instanceof SiweUserRejected) {
        setError('Signature cancelled in your wallet. Tap to try again.');
      } else {
        setError(e instanceof Error ? e.message : 'Could not connect your wallet.');
      }
      return { ok: false, holder: false };
    } finally {
      setConnecting(false);
    }
  }, [queryClient]);

  return { connect, connecting, error };
}
